import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useCampaignDetail } from "../hooks/useCampaigns";
import { useCampaignActionPlan, useCampaignPlanCatalog } from "../hooks/useCampaignActionPlans";
import {
  clearCampaignPlanRunIntent,
  readCampaignPlanRunIntent,
  useAdmitCampaignPlanRun,
  useCampaignPlanRunReadiness,
  useCampaignPlanRunRecovery,
  useCampaignPlanRuns,
  writeCampaignPlanRunIntent,
  type CampaignPlanRun,
  type CampaignPlanRunIntent,
} from "../hooks/useCampaignPlanRuns";
import { ApiError } from "../lib/api";
import { Markdown } from "../components/agent/Markdown";
import { buildBrandScopedPath } from "../lib/brand-navigation";

function scopedRoot(brandId: string, campaignId: string) {
  return `/brand/${encodeURIComponent(brandId)}/campaigns/${encodeURIComponent(campaignId)}`;
}

function runStatusLabel(status: CampaignPlanRun["status"]) {
  switch (status) {
    case "running": return "Running";
    case "retrying": return "Retrying";
    case "completed": return "Completed";
    case "failed": return "Failed";
    case "skipped": return "Skipped";
  }
}

function formatDate(value: string | null) {
  if (!value) return "—";
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleString();
}

export function CampaignPlanRunHistory({
  brandId,
  campaignId,
  planId,
}: {
  brandId: string;
  campaignId: string;
  planId: string;
}) {
  const history = useCampaignPlanRuns(brandId, campaignId, planId);
  const planQuery = useCampaignActionPlan(brandId, campaignId, planId);
  const currentRevision = planQuery.data?.data?.revision;
  const runs = Array.isArray(history.data?.data) ? history.data.data : null;
  const scopeMismatch = !!runs && !runs.every(run => run.brand_id === brandId && run.campaign_id === campaignId && run.plan_id === planId);
  return (
    <section className="space-y-4 border-t border-slate-200 pt-6" aria-labelledby="plan-runs-heading">
      <div>
        <h2 id="plan-runs-heading" className="text-lg font-semibold">Runs</h2>
        <p className="mt-1 text-sm text-slate-600">Runs from this saved plan.</p>
      </div>
      {history.isLoading ? <p role="status" className="text-sm text-slate-500">Loading Runs…</p>
        : history.error ? <div className="text-sm text-red-700" role="alert">Run history could not load. <button className="underline" type="button" onClick={() => void history.refetch()}>Retry history</button></div>
          : !runs || scopeMismatch ? <p role="alert" className="text-sm text-amber-900">Run history did not match this brand, campaign, and plan. Retry history before treating it as empty.</p>
            : !runs.length ? <p className="text-sm text-slate-500">No Runs are recorded for this plan.</p>
            : <ol className="divide-y divide-slate-200 rounded-lg border border-slate-200">
              {runs.map(run => <li key={run.id} className="flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
                <div className="min-w-0">
                  <p className="font-medium">{runStatusLabel(run.status)}{currentRevision !== undefined && run.plan_revision !== currentRevision && <span className="ml-2 rounded-full bg-slate-100 px-2 py-1 text-xs font-normal text-slate-600">Previous saved inputs</span>}</p>
                  <p className="mt-1 break-words text-sm text-slate-600">Started {formatDate(run.started_at)}{run.completed_at ? ` · finished ${formatDate(run.completed_at)}` : ""}</p>
                  {run.summary && <Markdown content={run.summary} className="mt-2 text-sm" />}
                  {run.error && <p className="mt-2 whitespace-pre-wrap break-words text-sm text-red-700">{run.error}</p>}
                </div>
                <Link className="inline-flex min-h-11 shrink-0 items-center rounded-lg border border-slate-300 px-4 text-sm font-medium" to={buildBrandScopedPath(`/runs/${encodeURIComponent(run.id)}`, brandId)}>Open Run</Link>
              </li>)}
            </ol>}
    </section>
  );
}

function makeIntent(revision: number): CampaignPlanRunIntent {
  return { invocation_id: crypto.randomUUID(), expected_revision: revision };
}

function errorMessage(error: unknown) {
  if (error instanceof ApiError) {
    if (error.status === 409) return "This saved plan or its run conditions changed. Reload the saved plan before confirming another Run.";
    if (error.status === 422) return error.message;
    return error.message;
  }
  return error instanceof Error ? error.message : "The Run request could not be confirmed.";
}

export function CampaignActionPlanRunPage() {
  const { brandId, campaignId, planId } = useParams();
  if (!brandId || !campaignId || !planId) return <p role="alert">This Run page is missing its campaign plan address.</p>;
  return <CampaignActionPlanRunScreen key={`${brandId}:${campaignId}:${planId}`} brandId={brandId} campaignId={campaignId} planId={planId} />;
}

function CampaignActionPlanRunScreen({ brandId, campaignId, planId }: { brandId: string; campaignId: string; planId: string }) {
  const navigate = useNavigate();
  const planQuery = useCampaignActionPlan(brandId, campaignId, planId);
  const catalogQuery = useCampaignPlanCatalog(brandId, campaignId);
  const campaignQuery = useCampaignDetail(brandId, campaignId);
  const [intent, setIntent] = useState<CampaignPlanRunIntent | null>(() => readCampaignPlanRunIntent(brandId, campaignId, planId));
  const recovery = useCampaignPlanRunRecovery(brandId, campaignId, planId, intent?.invocation_id);
  // A stored request is checked against immutable plan lineage before fresh readiness.
  const recoveryPending = !!intent && recovery.isLoading;
  const mayCheckFreshReadiness = !intent || (!recovery.isLoading && !recovery.error && recovery.data === null);
  const readiness = useCampaignPlanRunReadiness(
    mayCheckFreshReadiness ? brandId : undefined,
    mayCheckFreshReadiness ? campaignId : undefined,
    mayCheckFreshReadiness ? planId : undefined,
  );
  const admit = useAdmitCampaignPlanRun(brandId, campaignId, planId);
  const plan = planQuery.data?.data;
  const task = catalogQuery.data?.data.find(item => item.slug === plan?.task_slug);
  const campaign = campaignQuery.data?.data;
  const root = scopedRoot(brandId, campaignId);
  const planPath = `${root}/action-plans/${encodeURIComponent(planId)}`;
  const [message, setMessage] = useState<string | null>(null);
  const [needsReload, setNeedsReload] = useState(false);
  const [recoveryMismatch, setRecoveryMismatch] = useState(false);

  function matchesRequest(run: CampaignPlanRun, requestIntent: CampaignPlanRunIntent | null): boolean {
    return !!requestIntent
      && run.brand_id === brandId
      && run.campaign_id === campaignId
      && run.invocation_id === requestIntent.invocation_id
      && run.plan_id === planId
      && run.plan_revision === requestIntent.expected_revision
      && typeof run.id === "string"
      && run.id.length > 0;
  }

  useEffect(() => {
    const recovered = recovery.data;
    if (!recovered) return;
    if (!matchesRequest(recovered, intent)) {
      setRecoveryMismatch(true);
      return;
    }
    clearCampaignPlanRunIntent(brandId, campaignId, planId);
    navigate(buildBrandScopedPath(`/runs/${encodeURIComponent(recovered.id)}`, brandId), { replace: true });
  }, [recovery.data, brandId, campaignId, planId, navigate, intent]);

  const isOwnedPlan = !!plan && plan.id === planId && plan.campaign_id === campaignId && plan.brand_id === brandId;
  const isOwnedCampaign = !!campaign && campaign.id === campaignId && campaign.brand_id === brandId;
  const currentReadiness = readiness.data?.data;
  const revisionDrift = !!(plan && currentReadiness && currentReadiness.revision !== plan.revision);
  const definitionDrift = !!(plan && currentReadiness && currentReadiness.definition_version !== plan.definition_version);
  const intentDrift = !!(plan && intent && intent.expected_revision !== plan.revision);
  const exactReadiness = !!(plan && currentReadiness
    && currentReadiness.brand_id === brandId
    && currentReadiness.campaign_id === campaignId
    && currentReadiness.plan_id === plan.id
    && currentReadiness.revision === plan.revision
    && currentReadiness.definition_version === plan.definition_version);
  const blockers = useMemo(() => currentReadiness?.blockers ?? [], [currentReadiness]);

  async function confirmRun() {
    if (!plan || !currentReadiness?.can_run || !exactReadiness || revisionDrift || definitionDrift || intentDrift || needsReload) return;
    setMessage(null);
    let requestIntent = intent;
    if (!requestIntent) {
      try {
        requestIntent = makeIntent(plan.revision);
        writeCampaignPlanRunIntent(brandId, campaignId, planId, requestIntent);
      } catch {
        setMessage("The request identity could not be saved in this browser, so no Run was started. Check browser storage and try again.");
        return;
      }
      setIntent(requestIntent);
    }
    try {
      const response = await admit.mutateAsync(requestIntent);
      const admitted = response.data;
      if (admitted.brand_id !== brandId || admitted.campaign_id !== campaignId || admitted.plan_id !== planId || admitted.plan_revision !== requestIntent.expected_revision || admitted.invocation_id !== requestIntent.invocation_id || !admitted.run_id || !["running", "retrying", "completed", "failed", "skipped"].includes(admitted.status)) {
        setRecoveryMismatch(true);
        throw new Error("The server response did not match this saved plan request. Its admission status is uncertain; retry recovery before another Run.");
      }
      clearCampaignPlanRunIntent(brandId, campaignId, planId);
      setIntent(null);
      navigate(buildBrandScopedPath(`/runs/${encodeURIComponent(admitted.run_id)}`, brandId));
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) setNeedsReload(true);
      setMessage(errorMessage(error));
    }
  }

  async function reloadSavedPlan() {
    setMessage(null);
    if (intent) {
      if (recovery.isLoading || recovery.error) {
        const recoveredResult = await recovery.refetch();
        if (recoveredResult.error || recoveredResult.data !== null) {
          setMessage("The previous request has not been verified as absent. Its identity is retained; retry recovery before confirming a new request.");
          if (recoveredResult.data && matchesRequest(recoveredResult.data, intent)) {
            navigate(buildBrandScopedPath(`/runs/${encodeURIComponent(recoveredResult.data.id)}`, brandId), { replace: true });
          }
          return;
        }
      } else if (recovery.data !== null) {
        setMessage("The previous request has not been verified as absent. Its identity is retained; retry recovery before confirming a new request.");
        return;
      }
    }
    setNeedsReload(false);
    if (intent) {
      clearCampaignPlanRunIntent(brandId, campaignId, planId);
      setIntent(null);
    }
    setRecoveryMismatch(false);
    const [planResult, readinessResult] = await Promise.all([planQuery.refetch(), readiness.refetch()]);
    if (planResult.error || readinessResult.error) {
      setNeedsReload(true);
      setMessage("The saved plan could not be reloaded. No new Run request was sent.");
    }
  }

  const loading = planQuery.isLoading || campaignQuery.isLoading || recoveryPending;
  if (loading) return <section className="mx-auto w-full max-w-3xl py-8"><p role="status">Loading saved plan and checking for an admitted Run…</p></section>;
  if (planQuery.error || campaignQuery.error) return <section className="mx-auto w-full max-w-3xl space-y-4 py-8"><p role="alert">The saved plan or campaign could not be loaded.</p><button className="underline" type="button" onClick={() => { void planQuery.refetch(); void campaignQuery.refetch(); }}>Retry</button></section>;
  if (!isOwnedPlan || !isOwnedCampaign) return <section className="mx-auto w-full max-w-3xl space-y-4 py-8"><p role="alert">This saved plan does not belong to the campaign and brand in this address.</p><Link className="underline" to={`/brand/${encodeURIComponent(brandId)}/campaigns`}>Back to campaigns</Link></section>;

  return <main className="mx-auto w-full max-w-3xl space-y-7 py-6">
    <Link to={planPath} className="text-sm text-slate-600">← {campaign.title} · {plan.title}</Link>
    <header>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">Confirm campaign Run</p>
      <h1 className="mt-2 break-words text-3xl font-semibold">{plan.title}</h1>
      <p className="mt-3 whitespace-pre-wrap break-words leading-7 text-slate-700">{plan.objective}</p>
    </header>

    <section className="space-y-4 rounded-xl border border-slate-200 p-5">
      <h2 className="font-semibold">Saved plan snapshot</h2>
      <dl className="grid gap-4 text-sm sm:grid-cols-2">
        <div><dt className="text-slate-500">Campaign</dt><dd className="mt-1 font-medium">{campaign.title}</dd></div>
        <div><dt className="text-slate-500">Action</dt><dd className="mt-1 font-medium break-words">{task?.action.presentation.title ?? plan.task_slug.replace(/_/g, " ")}</dd></div>
        <div><dt className="text-slate-500">Operating window</dt><dd className="mt-1">{plan.starts_at ? `From ${formatDate(plan.starts_at)}` : "No start date"}<br />{plan.ends_at ? `Until ${formatDate(plan.ends_at)}` : "No end date"}</dd></div>
      </dl>
      <div className="border-t border-slate-200 pt-4">
        <h3 className="text-sm font-semibold">Inputs to be used</h3>
        {Object.keys(plan.params).length ? <dl className="mt-3 space-y-3">{Object.entries(plan.params).map(([key, value]) => <div key={key}><dt className="text-sm font-medium text-slate-700">{key.replace(/_/g, " ")}</dt><dd className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-600">{typeof value === "string" ? value : JSON.stringify(value)}</dd></div>)}</dl> : <p className="mt-2 text-sm text-slate-500">This Action has no saved input fields.</p>}
      </div>
    </section>

    <section className="space-y-4 rounded-xl bg-slate-50 p-5">
      <div><h2 className="font-semibold">Run readiness</h2><p className="mt-1 text-sm text-slate-600">The server reports whether this saved plan can run and lists any current blockers.</p></div>
      {recoveryMismatch ? <p role="alert" className="text-sm text-amber-900">The recovered record did not match this plan and request. Admission remains uncertain; retry recovery before sending another request. <button type="button" className="underline" onClick={() => void recovery.refetch()}>Retry recovery</button> <button type="button" className="underline" onClick={() => void reloadSavedPlan()}>Reload saved plan</button></p>
        : recovery.error ? <p role="alert" className="text-sm text-amber-900">A previous request may have been admitted, but its status could not be recovered. Retry the check before sending another request. <button type="button" className="underline" onClick={() => void recovery.refetch()}>Retry recovery</button></p>
        : readiness.isLoading ? <p role="status" className="text-sm text-slate-600">Checking whether this plan can run…</p>
          : readiness.error ? <p role="alert" className="text-sm text-amber-900">Readiness is unknown because the server could not be reached. No Run will be started until it can be checked. <button type="button" className="underline" onClick={() => void readiness.refetch()}>Retry readiness</button></p>
            : revisionDrift || definitionDrift || intentDrift ? <div role="alert" className="space-y-2 text-sm text-amber-900"><p>The saved plan or Action changed after this snapshot was loaded. Reload it to review the current inputs before confirming.</p><button className="underline" type="button" onClick={() => void reloadSavedPlan()}>Reload saved plan</button></div>
              : !exactReadiness ? <p role="alert" className="text-sm text-amber-900">The server readiness response did not match the loaded plan. Reload and check readiness again before confirming.</p>
              : currentReadiness ? <>
                <p className={`text-sm font-medium ${currentReadiness.can_run ? "text-emerald-800" : "text-amber-900"}`}>{currentReadiness.can_run ? "Ready to run" : "Cannot run yet"}</p>
                {!!blockers.length && <ul className="list-disc space-y-1 pl-5 text-sm text-amber-900">{blockers.map((blocker, index) => <li key={`${blocker.code}:${index}`}>{blocker.message}</li>)}</ul>}
              </> : null}
      <div className="border-t border-slate-200 pt-4 text-sm leading-6 text-slate-600">
        <p>Confirming starts one Run using the inputs shown here. It does not activate the campaign or create a schedule. Inputs are frozen for an admitted Run; later edits apply to future Runs.</p>
      </div>
    </section>

    {message && <p role="alert" className="rounded-lg bg-red-50 p-4 text-sm text-red-800">{message}{needsReload && <button type="button" className="ml-2 underline" onClick={() => void reloadSavedPlan()}>Reload saved plan</button>}</p>}
    <div className="flex flex-wrap justify-end gap-3 border-t border-slate-200 pt-5">
      <Link className="inline-flex min-h-11 items-center px-3 text-sm" to={planPath}>Cancel</Link>
      <button type="button" disabled={!currentReadiness?.can_run || !exactReadiness || !!readiness.error || !!recovery.error || recoveryMismatch || revisionDrift || definitionDrift || intentDrift || needsReload || admit.isPending} onClick={() => void confirmRun()} className="min-h-11 rounded-lg bg-brand-600 px-5 text-sm font-semibold text-white disabled:cursor-not-allowed disabled:opacity-50">{admit.isPending ? "Starting Run…" : "Confirm and run once"}</button>
    </div>
    <CampaignPlanRunHistory brandId={brandId} campaignId={campaignId} planId={planId} />
  </main>;
}
